import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  BookOpen,
  Mail,
  ArrowRight,
  Loader2,
  AlertCircle,
  Shield,
  Lock,
  Sparkles,
  GraduationCap,
  BookMarked,
  Users,
  CheckCircle
} from 'lucide-react';
import {
  discoverAuthenticationMethods,
  getActiveBooksellers,
  type AuthenticationDiscoveryDto,
  type BooksellerDto
} from '../api/booksellersService';
import { BooksellerLogo } from '../config/booksellerBranding';
import { oidcConfig } from '../config/auth.config';
import { useAppName, useAppIconUrl } from '../hooks/useTenantBranding';
import Logo from '../components/common/Logo';
import { useTranslation } from '../i18n/useTranslation';
import { LanguagePicker } from '../i18n/LanguagePicker';

// ABP Framework Account/Register URL
const REGISTER_URL = `${oidcConfig.authority}Account/Register?returnUrl=${encodeURIComponent(oidcConfig.redirect_uri)}`;

/**
 * Unified Login Page
 *
 * Handles three authentication scenarios:
 * 1. Local LearnLeaf account only → Password input
 * 2. Bookseller account(s) only → Bookseller OAuth
 * 3. Both local + bookseller(s) → Choice of LearnLeaf or bookseller
 *
 * Mobile-first design: Optimized for 320px+ screens
 * Visual design: Split-screen with branding panel on desktop
 * WCAG 2.2 AAA compliant with enhanced visual appeal
 */
export default function UnifiedLoginPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const appName = useAppName();
  const appIconUrl = useAppIconUrl();
  const { t } = useTranslation();

  // Email entry state
  const [email, setEmail] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  // Discovery results
  const [authMethods, setAuthMethods] = useState<AuthenticationDiscoveryDto | null>(null);

  // No password state needed - we redirect to OpenIddict for local auth

  // Loading states
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // First-time user flow: list of all active booksellers (populated when noAccountsFound)
  const [allBooksellers, setAllBooksellers] = useState<BooksellerDto[]>([]);
  const [searchParams] = useSearchParams();

  // Surface error-code banner from server-mediated OAuth flow redirects
  useEffect(() => {
    const err = searchParams.get('error');
    if (!err) return;
    const booksellerSlug = searchParams.get('bookseller');
    const booksellerLabel = booksellerSlug ?? 'the bookseller';
    if (err === 'unknown_provider') {
      setError(t('App.Auth.UnknownProvider'));
    } else if (err === 'cancelled') {
      setError(t('App.Auth.Cancelled'));
    } else if (err === 'provider_unreachable') {
      setError(t('App.Auth.ProviderUnreachable', { bookseller: booksellerLabel }));
    } else if (err === 'provisioning_failed') {
      setError(t('App.Auth.ProvisioningFailed', { bookseller: booksellerLabel }));
    }
  }, [searchParams, t]);

  /**
   * Step 1: Discover authentication methods for entered email
   */
  const handleDiscoverAuthMethods = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setError(t('App.Auth.EmailRequired'));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(t('App.Auth.EmailInvalid'));
      return;
    }

    setIsDiscovering(true);
    setError(null);
    setHasSearched(true);

    try {
      const methods = await discoverAuthenticationMethods(email);

      if (methods.noAccountsFound) {
        setAuthMethods(methods);
        try {
          const active = await getActiveBooksellers();
          setAllBooksellers(active);
        } catch (e) {
          console.warn('Failed to load active booksellers for first-time flow:', e);
        }
        setError(t('App.Auth.NoAccountsFound'));
        return;
      }

      // When the user has exactly one viable path, skip the chooser and go straight there.
      // Chooser only renders for genuinely ambiguous cases: multiple booksellers, or both
      // a local password and one-or-more booksellers.
      const booksellerCount = methods.booksellers.length;
      if (methods.hasLocalAccount && booksellerCount === 0) {
        handleLocalLogin();
        return;
      }
      if (!methods.hasLocalAccount && booksellerCount === 1) {
        handleBooksellerLogin(methods.booksellers[0]);
        return;
      }

      setAuthMethods(methods);
    } catch (err: any) {
      console.error('Failed to discover auth methods:', err);
      setError(
        err?.response?.data?.error?.message ||
        t('App.Auth.DiscoveryFailed')
      );
    } finally {
      setIsDiscovering(false);
    }
  };

  /**
   * Step 2a: Local authentication (LearnLeaf account)
   * Redirects to OpenIddict login page with email pre-filled
   */
  const handleLocalLogin = () => {
    setIsAuthenticating(true);
    auth.signinRedirect({
      extraQueryParams: {
        login_hint: email, // Pre-fill email in OIDC login form
      },
    });
  };

  /**
   * Step 2b: Bookseller authentication (OAuth)
   */
  const handleBooksellerLogin = (bookseller: BooksellerDto) => {
    setIsAuthenticating(true);
    setError(null);
    const returnUrl = '/my-library';
    window.location.href = `/Account/ExternalLogin?provider=${encodeURIComponent(bookseller.slug)}&returnUrl=${encodeURIComponent(returnUrl)}`;
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Panel - Branding/Marketing (Hidden on mobile, visible on desktop) */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-2/5 p-12 flex-col justify-between relative overflow-hidden bg-gradient-to-br from-primary-600 to-secondary-600">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2" />
          <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-secondary-300 rounded-full blur-2xl transform -translate-x-1/2 -translate-y-1/2" />
        </div>

        {/* Content */}
        <div className="relative z-10">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-16">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-xl border border-white/30 overflow-hidden">
              <Logo variant="icon" size="xs" alt={appName} customIconUrl={appIconUrl} />
            </div>
            <h1 className="text-3xl font-bold text-white">{appName}</h1>
          </div>

          {/* Main Content */}
          <div className="space-y-8">
            <div>
              <h2 className="text-4xl xl:text-5xl font-bold text-white mb-4 leading-tight">
                {t('App.Auth.BrandingTitle1')}
                <br />
                {t('App.Auth.BrandingTitle2')}
                <br />
                <span className="text-primary-200">{t('App.Auth.BrandingTitle3')}</span>
              </h2>
              <p className="text-xl text-primary-100 font-light">
                {t('App.Auth.BrandingSubtitle')}
              </p>
            </div>

            {/* Features */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0">
                  <BookMarked className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">{t('App.Auth.FeatureLibrary')}</h3>
                  <p className="text-primary-100 text-sm">{t('App.Auth.FeatureLibraryDesc')}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">{t('App.Auth.FeatureSecure')}</h3>
                  <p className="text-primary-100 text-sm">{t('App.Auth.FeatureSecureDesc')}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">{t('App.Auth.FeatureInstitution')}</h3>
                  <p className="text-primary-100 text-sm">{t('App.Auth.FeatureInstitutionDesc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Stats */}
        <div className="relative z-10 grid grid-cols-3 gap-6 pt-8 border-t border-white/20">
          <div>
            <p className="text-3xl font-bold text-white">50K+</p>
            <p className="text-sm text-primary-100">{t('App.Auth.StatStudents')}</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-white">500+</p>
            <p className="text-sm text-primary-100">{t('App.Auth.StatInstitutions')}</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-white">10K+</p>
            <p className="text-sm text-primary-100">{t('App.Auth.StatTitles')}</p>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-20 right-10 text-white/10">
          <Sparkles className="w-24 h-24" />
        </div>
        <div className="absolute bottom-20 left-10 text-white/10">
          <GraduationCap className="w-32 h-32" />
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex flex-col min-h-screen bg-gradient-to-br from-neutral-50 via-white to-primary-50">
        {/* Mobile Header */}
        <header className="lg:hidden bg-gradient-to-r from-primary-600 to-secondary-600 px-6 py-8" role="banner">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg border border-white/30 overflow-hidden">
              <Logo variant="icon" size="xs" alt={appName} customIconUrl={appIconUrl} />
            </div>
            <h1 className="text-2xl font-bold text-white flex-1">{appName}</h1>
            <div className="[&_button]:text-white/80 [&_button:hover]:text-white">
              <LanguagePicker />
            </div>
          </div>
          <p className="text-primary-100">{t('App.Auth.DigitalCoursewareHub')}</p>
        </header>

        {/* Sign Up Link - Desktop Top Right */}
        <div className="hidden lg:flex justify-end items-center gap-4 p-6">
          <LanguagePicker />
          <p className="text-neutral-600">
            {t('App.Auth.DontHaveAccount')}{' '}
            <a
              href={REGISTER_URL}
              className="text-primary-600 hover:text-primary-700 font-semibold underline-offset-2 hover:underline transition-colors"
              aria-label={t('App.Auth.SignUpAriaLabel', { appName })}
            >
              {t('App.Auth.SignUp')}
            </a>
          </p>
        </div>

        {/* Main Form Container */}
        <div className="flex-1 flex items-center justify-center p-6 sm:p-8 lg:p-12">
          <div className="w-full max-w-md space-y-8">
            {/* Desktop Header (Hidden on mobile since we have the gradient header) */}
            <div className="hidden lg:block text-center">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-neutral-900 to-neutral-700 bg-clip-text text-transparent mb-3">
                {t('App.Auth.WelcomeBack')}
              </h1>
              <p className="text-neutral-600 text-lg">
                {!hasSearched || !authMethods
                  ? t('App.Auth.SignInSubtitle')
                  : authMethods.hasLocalAccount && authMethods.booksellers.length > 0
                  ? t('App.Auth.ChooseSignIn')
                  : authMethods.hasLocalAccount
                  ? t('App.Auth.SignInWithApp', { appName })
                  : t('App.Auth.SignInWithBookseller')}
              </p>
            </div>

            {/* Mobile Header Text */}
            <div className="lg:hidden text-center">
              <h2 className="text-3xl font-bold text-neutral-900 mb-2">
                {t('App.Auth.WelcomeBack')}
              </h2>
              <p className="text-neutral-600">
                {!hasSearched || !authMethods
                  ? t('App.Auth.SignInSubtitleMobile')
                  : authMethods.hasLocalAccount && authMethods.booksellers.length > 0
                  ? t('App.Auth.ChooseSignIn')
                  : authMethods.hasLocalAccount
                  ? t('App.Auth.SignInWithApp', { appName })
                  : t('App.Auth.SignInWithBookseller')}
              </p>
            </div>

            {/* Email Entry Form (Step 1) */}
            {!hasSearched || !authMethods || authMethods.noAccountsFound ? (
              <div className="relative">
                {/* Card with gradient border effect */}
                <div className="absolute -inset-0.5 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-2xl opacity-20 blur-lg" />
                <div className="relative bg-white rounded-2xl shadow-2xl p-8 border border-neutral-100">
                  <form onSubmit={handleDiscoverAuthMethods} className="space-y-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-neutral-700 mb-2">
                        {t('App.Auth.EmailAddress')}
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-0 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-lg opacity-0 group-hover:opacity-10 transition-opacity" />
                        <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400 z-10" />
                        <input
                          id="email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="student@university.edu"
                          className="relative w-full pl-12 pr-4 py-3.5 bg-neutral-50 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent focus:bg-white transition-all text-neutral-900 placeholder-neutral-400"
                          style={{ minHeight: '48px' }}
                          disabled={isDiscovering}
                          autoFocus
                          required
                          autoComplete="email"
                          aria-label={t('App.Auth.EnterEmail')}
                          aria-describedby="email-description"
                        />
                      </div>
                      <p id="email-description" className="mt-2 text-sm text-neutral-500">
                       {/* We'll find your account and show you how to sign in */}
                      </p>
                    </div>

                    {error && (
                      <div className="flex items-start gap-3 p-4 bg-error-50 border border-error-200 rounded-xl" role="alert">
                        <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                        <p className="text-sm text-error-800">{error}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isDiscovering || isAuthenticating}
                      className="relative w-full group"
                      style={{ minHeight: '48px' }}
                    >
                      <div className="absolute -inset-0.5 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-lg blur opacity-75 group-hover:opacity-100 transition duration-200" />
                      <div className="relative flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg hover:from-primary-700 hover:to-primary-800 disabled:from-neutral-300 disabled:to-neutral-400 disabled:cursor-not-allowed transition-all font-semibold shadow-lg">
                        {isDiscovering || isAuthenticating ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                            <span>{isAuthenticating ? t('App.Auth.Redirecting') : t('App.Auth.Searching')}</span>
                          </>
                        ) : (
                          <>
                            <span>{t('App.Auth.Continue')}</span>
                            <ArrowRight className="w-5 h-5" aria-hidden="true" />
                          </>
                        )}
                      </div>
                    </button>
                  </form>

                  {authMethods?.noAccountsFound && allBooksellers.length > 0 && (
                    <div className="mt-6 space-y-3">
                      <p className="text-sm text-neutral-700 font-medium text-center">
                        {t('App.Auth.FirstTimeHere')}
                      </p>
                      {allBooksellers.map((bookseller) => (
                        <button
                          key={bookseller.id}
                          type="button"
                          onClick={() => handleBooksellerLogin(bookseller)}
                          disabled={isAuthenticating}
                          className="w-full flex items-center gap-3 px-4 py-3 bg-white border-2 border-neutral-200 rounded-lg hover:border-primary-400 disabled:opacity-50 transition-all"
                          style={{ minHeight: '56px' }}
                        >
                          <BooksellerLogo slug={bookseller.slug} size="md" />
                          <span className="flex-1 text-left font-semibold text-neutral-900">{bookseller.name}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Security Note with enhanced visual */}
                  {/* <div className="mt-8 p-4 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-xl border border-primary-100">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Shield className="w-4 h-4 text-primary-600" aria-hidden="true" />
                      </div>
                      <div className="text-sm">
                        <p className="font-semibold text-neutral-800 mb-1">Secure Authentication</p>
                        <p className="text-neutral-600">
                          Your credentials are protected with industry-standard encryption. We never store passwords from external providers.
                        </p>
                      </div>
                    </div>
                  </div> */}

                  {/* Sign Up Link for Mobile */}
                  <div className="lg:hidden mt-6 pt-6 border-t border-neutral-200 text-center">
                    <p className="text-neutral-600">
                      {t('App.Auth.DontHaveAccount')}{' '}
                      <a
                        href={REGISTER_URL}
                        className="text-primary-600 hover:text-primary-700 font-semibold underline-offset-2 hover:underline"
                        aria-label={t('App.Auth.SignUpAriaLabel', { appName })}
                      >
                        {t('App.Auth.SignUp')}
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              /* Authentication Options (Step 2) */
              <div className="relative">
                {/* Card with gradient border effect */}
                <div className="absolute -inset-0.5 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-2xl opacity-20 blur-lg" />
                <div className="relative bg-white rounded-2xl shadow-2xl p-8 border border-neutral-100 space-y-6">
                  {/* Scenario 1 & 3: Local Account (Platform OIDC) */}
                  {authMethods.hasLocalAccount && (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-primary-50 to-secondary-50 border border-primary-200 rounded-xl">
                        <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Lock className="w-5 h-5 text-primary-600" aria-hidden="true" />
                        </div>
                        <div className="text-sm">
                          <p className="font-semibold text-neutral-900">{t('App.Auth.AppAccount', { appName })}</p>
                          <p className="text-neutral-600">{email}</p>
                        </div>
                        <CheckCircle className="w-5 h-5 text-success-500 ml-auto" aria-hidden="true" />
                      </div>

                      <button
                        onClick={handleLocalLogin}
                        disabled={isAuthenticating}
                        className="relative w-full group"
                        style={{ minHeight: '48px' }}
                        aria-label={t('App.Auth.SignInWithApp', { appName })}
                      >
                        <div className="absolute -inset-0.5 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-lg blur opacity-75 group-hover:opacity-100 transition duration-200" />
                        <div className="relative flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg hover:from-primary-700 hover:to-primary-800 disabled:from-neutral-300 disabled:to-neutral-400 disabled:cursor-not-allowed transition-all font-semibold shadow-lg">
                          {isAuthenticating ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                              <span>{t('App.Auth.Redirecting')}</span>
                            </>
                          ) : (
                            <>
                              <BookOpen className="w-5 h-5" aria-hidden="true" />
                              <span>{t('App.Auth.SignInWithAppButton', { appName })}</span>
                            </>
                          )}
                        </div>
                      </button>

                      <p className="text-xs text-center text-neutral-500">
                        {t('App.Auth.PasswordRedirect')}
                      </p>
                    </div>
                  )}

                  {/* Divider (for scenario 3: both local + bookseller) */}
                  {authMethods.hasLocalAccount && authMethods.booksellers.length > 0 && (
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-neutral-200"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-4 bg-white text-neutral-500 font-medium">{t('App.Auth.OrContinueWith')}</span>
                      </div>
                    </div>
                  )}

                  {/* Scenario 2 & 3: Bookseller Accounts */}
                  {authMethods.booksellers.length > 0 && (
                    <div className="space-y-3">
                      <p className="text-sm text-neutral-600 font-medium">{t('App.Auth.SelectInstitution')}</p>
                      {authMethods.booksellers.map((bookseller) => (
                        <button
                          key={bookseller.id}
                          onClick={() => handleBooksellerLogin(bookseller)}
                          disabled={isAuthenticating}
                          className="relative w-full group"
                          style={{ minHeight: '72px' }}
                          aria-label={t('App.Auth.SignInWithProvider', { provider: bookseller.name })}
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-xl opacity-0 group-hover:opacity-10 transition-opacity" />
                          <div className="relative flex items-center gap-4 p-4 bg-neutral-50 border-2 border-neutral-200 rounded-xl hover:border-primary-400 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-all">
                            <div className="group-hover:scale-110 transition-transform">
                              <BooksellerLogo slug={bookseller.slug} size="md" />
                            </div>

                            <div className="flex-1 text-left">
                              <h3 className="font-semibold text-neutral-900 group-hover:text-primary-600 transition-colors">
                                {bookseller.name}
                              </h3>
                              {bookseller.description && (
                                <p className="text-sm text-neutral-600 line-clamp-1">
                                  {bookseller.description}
                                </p>
                              )}
                            </div>

                            {isAuthenticating ? (
                              <Loader2 className="w-5 h-5 text-neutral-400 animate-spin" aria-hidden="true" />
                            ) : (
                              <ArrowRight className="w-5 h-5 text-neutral-400 group-hover:text-primary-600 transition-colors" aria-hidden="true" />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {error && (
                    <div className="flex items-start gap-3 p-4 bg-error-50 border border-error-200 rounded-xl" role="alert">
                      <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <p className="text-sm text-error-800">{error}</p>
                    </div>
                  )}

                  {/* Change Email */}
                  <div className="pt-4 border-t border-neutral-200">
                    <button
                      onClick={() => {
                        setHasSearched(false);
                        setAuthMethods(null);
                        setError(null);
                      }}
                      className="w-full text-sm text-primary-600 hover:text-primary-700 font-semibold transition-colors hover:underline underline-offset-2"
                      aria-label={t('App.Auth.ChangeEmailAriaLabel')}
                    >
                      {t('App.Auth.UseDifferentEmail')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Footer Help Text */}
            <div className="text-center space-y-2">
              <p className="text-sm text-neutral-500">
                {t('App.Auth.NeedAssistance')}{' '}
                <a
                  href="#"
                  className="text-primary-600 hover:text-primary-700 font-medium underline-offset-2 hover:underline"
                  aria-label={t('App.Auth.ContactSupport')}
                >
                  {t('App.Auth.ContactSupport')}
                </a>
              </p>
              <p className="text-xs text-neutral-400">
                {t('App.Auth.AllRightsReserved', { appName })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
